import { Info } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useCompanies } from '@/features/companies/api'
import { CompanySwitcher } from '@/features/companies/components/CompanySwitcher'
import { CompanySpine } from '@/shared/components/common'
import { PageHeader } from '@/shared/components/common/PageHeader'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { SortableList } from '@/shared/components/dnd'
import { useT } from '@/shared/i18n'

import { useDeleteStage, useReorderStages, useStageSets, useUpdateStage } from './api'
import { SettingsNav } from './components/SettingsNav'
import { StageDeleteDialog } from './components/StageDeleteDialog'
import { StageRow } from './components/StageRow'
import type { CompanySummary } from '@/shared/types/domain'

import type { StageSet, StageSetKind, StageSetting } from './types'

/**
 * Las dos pantallas, en CLAVES del catálogo.
 *
 * `itemsKey` apunta a una entrada con formas plurales —"1 proyecto" / "4
 * proyectos"— porque el diálogo de borrado cuenta lo que hay adentro y el
 * plural no se arma con un ternario: en portugués el 0 va en singular.
 */
const COPY: Record<StageSetKind, { titleKey: string; descriptionKey: string; itemsKey: string }> = {
  project: {
    titleKey: 'settings.stages.project.title',
    descriptionKey: 'settings.stages.project.description',
    itemsKey: 'settings.stages.hasItems.project',
  },
  task: {
    titleKey: 'settings.stages.task.title',
    descriptionKey: 'settings.stages.task.description',
    itemsKey: 'settings.stages.hasItems.task',
  },
}

export interface StageSettingsPageProps {
  kind: StageSetKind
}

/**
 * `/settings/fases` y `/settings/estados-de-tarea`.
 *
 * Es la misma pantalla con dos datasets: un set de columnas es un set de
 * columnas, y tener dos pantallas casi iguales garantiza que una arregle un bug
 * que la otra se queda.
 */
export function StageSettingsPage({ kind }: StageSettingsPageProps) {
  const t = useT()
  const copy = COPY[kind]
  const query = useStageSets(kind)
  const { data: companies = [] } = useCompanies()

  /**
   * Cada empresa tiene SU set, y todos se llaman igual.
   *
   * El set se clona de la plantilla al crear el primer proyecto de la empresa,
   * asi que el nombre (`Fases de proyecto`) se repite tantas veces como empresas
   * haya. La pantalla mostraba ocho bloques identicos cuyo unico subtitulo era
   * "Set propio de la empresa" — sin decir de cual. Resolver la empresa aca deja
   * el nombre y la espina en la cabecera de cada bloque, que es el mecanismo que
   * el resto de la app ya usa para contestar esa misma pregunta.
   */
  const porEmpresa = useMemo(
    () => new Map(companies.map((company) => [company.id, company])),
    [companies],
  )

  const [company, setCompany] = useState<string | null>(null)

  const sets = useMemo(() => {
    const todos = query.data ?? []
    // La plantilla del sistema se queda SIEMPRE: es de la que salen las demas, y
    // esconderla al filtrar dejaria la pantalla sin la referencia.
    return company ? todos.filter((set) => set.is_system || set.company_id === company) : todos
  }, [query.data, company])

  return (
    <div className="flex min-h-dvh flex-col">
      <PageHeader title={t(copy.titleKey)} description={t(copy.descriptionKey)}>
        <SettingsNav />
      </PageHeader>

      <div className="flex flex-1 flex-col gap-4 px-4 py-3">
        <CategoryNote />

        <div className="flex flex-wrap items-center gap-2">
          <CompanySwitcher value={company} onChange={setCompany} />
          <span className="text-2xs text-text-3">{t('settings.stages.companyHint')}</span>
        </div>

        {query.isPending ? (
          <SkeletonRows rows={6} columns={[0.3, 0.2, 0.15, 0.1]} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : sets.length === 0 ? (
          <EmptyState
            title={t('settings.stages.emptyTitle')}
            description={t('settings.stages.emptyDescription')}
          />
        ) : (
          sets.map((set) => (
            <StageSetCard
              key={set.id}
              set={set}
              company={set.company_id ? (porEmpresa.get(set.company_id) ?? null) : null}
              kind={kind}
              itemsKey={copy.itemsKey}
            />
          ))
        )}
      </div>
    </div>
  )
}

/**
 * Por qué la categoría está bloqueada.
 *
 * No alcanza con deshabilitar el control: alguien que no sabe por qué no puede
 * tocar algo termina pidiendo que se lo habiliten. Esto explica el mecanismo en
 * una línea, que es lo que hace que la restricción se entienda en vez de
 * molestar.
 */
function CategoryNote() {
  const t = useT()

  // El párrafo es UNA clave, sin palabras resaltadas adentro: destacar
  // "nombre", "color" y "categoría" pedía cortar la frase en pedazos, y el
  // orden de esos pedazos cambia en cada idioma.
  return (
    <p
      data-testid="category-note"
      className="hairline flex max-w-[92ch] items-start gap-2 rounded-[var(--radius-card)] bg-surface-1 px-3 py-2.5 text-xs text-text-2"
    >
      <Info size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-text-3" />
      <span>{t('settings.stages.categoryNote')}</span>
    </p>
  )
}

interface StageSetCardProps {
  set: StageSet
  /**
   * La empresa duena del set, ya resuelta. `null` en la plantilla del sistema.
   *
   * Puede ser `null` tambien si el listado de empresas todavia no llego; en ese
   * caso la cabecera cae al nombre del set, que es lo que mostraba antes. Lo que
   * NO hace es inventar un nombre.
   */
  company: CompanySummary | null
  kind: StageSetKind
  /** Clave con formas plurales para contar lo que vive en una columna. */
  itemsKey: string
}

function StageSetCard({ set, company, kind, itemsKey }: StageSetCardProps) {
  const t = useT()
  const update = useUpdateStage(kind)
  const reorder = useReorderStages(kind)
  const remove = useDeleteStage(kind)

  const [deleting, setDeleting] = useState<StageSetting | null>(null)

  const stages = set.stages
  const target = deleting ? (stages.find((stage) => stage.id === deleting.id) ?? deleting) : null

  return (
    <section className="flex flex-col gap-2">
      {/*
        DE QUIEN SON ESTAS FASES.
        El titulo es la EMPRESA, no el nombre del set: el nombre del set es el
        mismo en todos (`Fases de proyecto`) y por eso la pantalla se leia como
        una lista de bloques repetidos. La espina de color va adelante, que es
        como se identifica una empresa en el resto de la app.
      */}
      <header className="flex min-h-5 flex-wrap items-center gap-2">
        {company ? (
          <CompanySpine hue={company.color_hue} label={company.name} className="h-4" />
        ) : null}
        <h2 className="font-display text-base text-text-1">{company?.name ?? set.name}</h2>
        <span className="text-2xs text-text-3">
          {set.is_system ? t('settings.stages.systemSet') : t('settings.stages.ownSet')}
        </span>
      </header>

      <SortableList
        items={stages}
        ariaLabel={t('settings.stages.columnsOf', { set: company?.name ?? set.name })}
        className="hairline overflow-hidden rounded-[var(--radius-card)]"
        getLabel={(stage) => stage.name}
        onReorder={({ itemId, newIndex }) => {
          const rest = stages.filter((stage) => stage.id !== itemId)
          const next = [
            ...rest.slice(0, newIndex).map((stage) => stage.id),
            itemId,
            ...rest.slice(newIndex).map((stage) => stage.id),
          ]
          reorder.mutate({ stageSetId: set.id, stageIds: next })
        }}
        renderItem={(stage, state) => (
          <StageRow
            stage={stage}
            handle={state.handle}
            isDragging={state.isDragging}
            saving={update.isPending}
            canDelete={stages.length > 1}
            onSave={(patch) => update.mutate({ stageId: stage.id, patch })}
            onDelete={() => setDeleting(stage)}
          />
        )}
      />

      {target ? (
        <StageDeleteDialog
          stage={target}
          others={stages.filter((stage) => stage.id !== target.id)}
          itemsKey={itemsKey}
          pending={remove.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={(moveTo) => {
            remove.mutate({ stageId: target.id, moveTo }, { onSuccess: () => setDeleting(null) })
          }}
        />
      ) : null}
    </section>
  )
}
